import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { FiMail, FiPhoneCall, FiSearch, FiExternalLink, FiInbox, FiX, FiRefreshCw, FiClock, FiMessageSquare } from 'react-icons/fi';
import { ROUTES } from '../../app/routes';
import { useNavigate } from 'react-router-dom';
import { fetchCommunicationPage, subscribeToCommunicationUpdates, type CommItem, } from '../../services/communicationService';
import { checkEmailIntegration } from '../../services/centralizedEmailService';
import { getCallHistoryDF, type DfCallHistory } from '../../services/voiceDataFabricService';
import { useCommunicationStats } from '../../context/CommunicationStatsContext';
import '../../styles/MomentumCommand.css';
import '../../styles/CommunicationCenter.css';
type Tab = 'all' | 'email' | 'voice' | 'calls';
// Pages of the task feed, which carries every source; only Email/Voice rows
// are kept, so a page can add fewer rows than this.
const PAGE_SIZE = 50;
// Keep loading until the current tab has at least this many rows to show
// (or the feed runs out), so a page of mostly Manual/AI tasks doesn't leave
// the list looking empty with nothing to scroll.
const MIN_VISIBLE_ROWS = 20;
function relTime(iso?: string | null): string {
    if (!iso)
        return '—';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime()))
        return '—';
    const mins = Math.round((Date.now() - d.getTime()) / 60000);
    if (mins < 1)
        return 'just now';
    if (mins < 60)
        return `${mins}m ago`;
    const hrs = Math.round(mins / 60);
    if (hrs < 24)
        return `${hrs}h ago`;
    const days = Math.round(hrs / 24);
    if (days < 7)
        return `${days}d ago`;
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}
const STATUS_LABEL: Record<CommItem['status'], string> = {
    todo: 'Needs a reply', inprogress: 'In progress', inreview: 'In review', done: 'Resolved',
};
// Same status colours as the Command Center list.
const STATUS_COLOR: Record<CommItem['status'], string> = {
    todo: '#60a5fa', inprogress: '#f59e0b', inreview: '#22d3ee', done: '#34d399',
};
const taskHref = (item: CommItem) => `${ROUTES.MOMENTUM_COMMAND}/${encodeURIComponent(item.transactionId || item.taskId)}`;
const SkeletonRows: React.FC<{
    count?: number;
}> = ({ count = 6 }) => (<>
    {Array.from({ length: count }, (_, i) => (<div key={i} className="mc-skel-row">
        <div className="mc-skel-bone" style={{ width: 44, height: 10, flexShrink: 0 }}/>
        <div className="mc-skel-bone" style={{ width: 70, height: 18, borderRadius: 6, flexShrink: 0 }}/>
        <div className="mc-skel-bone" style={{ flex: 1, minWidth: 0, height: 12 }}/>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexShrink: 0 }}>
          <div className="mc-skel-bone" style={{ width: 90, height: 18, borderRadius: 6 }}/>
          <div className="mc-skel-bone" style={{ width: 50, height: 11 }}/>
          <div className="mc-skel-bone" style={{ width: 22, height: 11 }}/>
        </div>
      </div>))}
  </>);
const CommunicationCenterPage: React.FC = () => {
    const navigate = useNavigate();
    const { setStats } = useCommunicationStats();
    const [items, setItems] = useState<CommItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [hasMore, setHasMore] = useState(false);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [emailConnected, setEmailConnected] = useState<boolean | null>(null);
    const [calls, setCalls] = useState<DfCallHistory[]>([]);
    const [tab, setTab] = useState<Tab>('all');
    const [query, setQuery] = useState('');
    // Server-side search term (debounced copy of `query`).
    const [search, setSearch] = useState('');
    const pageRef = useRef(0);
    const fetchSeqRef = useRef(0);
    const busyRef = useRef(false);
    const listRef = useRef<HTMLDivElement | null>(null);
    const sentinelRef = useRef<HTMLDivElement | null>(null);
    const loadPage = useCallback((pageNum: number) => {
        const seq = pageNum === 1 ? ++fetchSeqRef.current : fetchSeqRef.current;
        busyRef.current = true;
        if (pageNum === 1)
            setLoading(true);
        else
            setLoadingMore(true);
        fetchCommunicationPage(pageNum, PAGE_SIZE, search)
            .then(res => {
            if (seq !== fetchSeqRef.current)
                return;
            pageRef.current = pageNum;
            setItems(prev => {
                if (pageNum === 1)
                    return res.items;
                const seen = new Set(prev.map(i => i.taskId));
                return [...prev, ...res.items.filter(i => !seen.has(i.taskId))];
            });
            setHasMore(res.hasMore);
            setLoadError(null);
        })
            .catch(() => {
            if (seq !== fetchSeqRef.current)
                return;
            // Keep what is already on screen; only an empty first load is an error state.
            if (pageNum === 1)
                setLoadError('Could not reach the task feed. Try again shortly.');
            setHasMore(false);
        })
            .finally(() => {
            if (seq !== fetchSeqRef.current)
                return;
            busyRef.current = false;
            setLoading(false);
            setLoadingMore(false);
        });
    }, [search]);
    const loadNextPage = useCallback(() => {
        if (busyRef.current || !hasMore)
            return;
        loadPage(pageRef.current + 1);
    }, [hasMore, loadPage]);
    // First page, and again whenever the search term changes.
    useEffect(() => {
        loadPage(1);
    }, [loadPage]);
    // Live updates: refresh the first page and keep the older pages already loaded.
    useEffect(() => {
        const refresh = () => {
            const seq = fetchSeqRef.current;
            fetchCommunicationPage(1, PAGE_SIZE, search)
                .then(res => {
                if (seq !== fetchSeqRef.current)
                    return;
                setItems(prev => {
                    const fresh = new Set(res.items.map(i => i.taskId));
                    return [...res.items, ...prev.filter(i => !fresh.has(i.taskId))];
                });
            })
                .catch(() => { });
        };
        return subscribeToCommunicationUpdates(refresh);
    }, [search]);
    useEffect(() => {
        const t = setTimeout(() => setSearch(query.trim()), 400);
        return () => clearTimeout(t);
    }, [query]);
    useEffect(() => {
        checkEmailIntegration().then(r => setEmailConnected(r.hasIntegration)).catch(() => setEmailConnected(null));
    }, []);
    useEffect(() => {
        getCallHistoryDF().then(setCalls).catch(() => setCalls([]));
    }, []);
    const emailCount = items.filter(i => i.source === 'email').length;
    const voiceCount = items.filter(i => i.source === 'voice').length;
    const needsReplyCount = items.filter(i => i.status === 'todo' || i.status === 'inprogress').length;
    const visibleItems = useMemo(() => {
        const list = tab === 'email' || tab === 'voice' ? items.filter(i => i.source === tab) : items;
        return list.slice().sort((a, b) => (b.updatedAt || '').localeCompare(a.updatedAt || ''));
    }, [items, tab]);
    const q = query.trim().toLowerCase();
    const visibleCalls = useMemo(() => {
        if (!q)
            return calls;
        return calls.filter(c => [c.firstName, c.lastName, c.phoneNumber, c.transcriptSummary]
            .some(v => (v || '').toLowerCase().includes(q)));
    }, [calls, q]);
    // Fill the current tab: a page may hold few or no Email/Voice tasks.
    useEffect(() => {
        if (tab === 'calls' || loading || loadingMore || !hasMore)
            return;
        if (visibleItems.length < MIN_VISIBLE_ROWS)
            loadNextPage();
    }, [tab, loading, loadingMore, hasMore, visibleItems.length, loadNextPage]);
    // Infinite scroll, as in Command Center: load the next page near the bottom.
    useEffect(() => {
        const el = sentinelRef.current;
        const root = listRef.current;
        if (!el || !root || tab === 'calls' || !hasMore)
            return;
        const io = new IntersectionObserver(entries => {
            if (entries.some(e => e.isIntersecting))
                loadNextPage();
        }, { root, rootMargin: '200px' });
        io.observe(el);
        return () => io.disconnect();
    }, [tab, hasMore, loadNextPage, visibleItems.length]);
    useEffect(() => {
        setStats({
            inboxCount: emailCount + voiceCount,
            needsReplyCount,
            recentCallsCount: calls.length,
        });
    }, [emailCount, voiceCount, needsReplyCount, calls.length, setStats]);
    const more = hasMore ? '+' : '';
    const tabs: Array<[Tab, string, React.ReactNode, string]> = [
        ['all', 'All', <FiInbox size={13} key="i"/>, `${emailCount + voiceCount}${more}`],
        ['email', 'Email', <FiMail size={13} key="m"/>, `${emailCount}${more}`],
        ['voice', 'Voice', <FiPhoneCall size={13} key="p"/>, `${voiceCount}${more}`],
        ['calls', 'Recent calls', <FiClock size={13} key="c"/>, String(calls.length)],
    ];
    const openItem = (item: CommItem) => navigate(taskHref(item));
    return (<div className="mc-page comm-page">
      <div className="mc-titlebar">
        <div className="mc-titlebar__text">
          <h1 className="mc-title">Communication Center</h1>
          {!loading && !loadError && (<p className="mc-subtitle">
              <span className="mc-subtitle__num">{needsReplyCount}</span> need a reply
              <span className="mc-subtitle__sep" aria-hidden="true">·</span>
              <span className="mc-subtitle__num">{emailCount}</span> email
              <span className="mc-subtitle__sep" aria-hidden="true">·</span>
              <span className="mc-subtitle__num">{voiceCount}</span> voice
            </p>)}
        </div>
      </div>

      {emailConnected === false && (<div className="comm__nudge comm-page__nudge">
          <FiMail size={16} aria-hidden="true"/>
          <span className="comm__nudge-copy">
            <span className="comm__nudge-title">Connect your inbox</span>
            <span className="comm__nudge-sub">Link Gmail, Exchange, or IMAP to start syncing email here.</span>
          </span>
          <button type="button" className="comm__nudge-btn" onClick={() => navigate(ROUTES.SETTINGS_INTEGRATIONS)}>
            Connect <FiExternalLink size={12} aria-hidden="true"/>
          </button>
        </div>)}

      <div className="mc-header">
        <div className="mc-header__filters">
          <div className="mc-filter-bar">
            <div className="mc-header__search">
              <FiSearch className="mc-search__icon" size={14}/>
              <input placeholder={tab === 'calls' ? 'Search calls…' : 'Search threads, contacts, accounts…'} aria-label="Search communication" value={query} onChange={e => setQuery(e.target.value)}/>
              {query && (<button className="mc-search__clear" title="Clear search" onClick={() => setQuery('')}>
                  <FiX size={11}/>
                </button>)}
            </div>
          </div>
          <div className="cc-assign-seg" role="tablist" aria-label="Channel">
            {tabs.map(([key, label, icon, count], idx) => (<React.Fragment key={key}>
                {idx > 0 && <span className="cc-assign-seg__divider" aria-hidden="true"/>}
                <button type="button" role="tab" aria-selected={tab === key} className={`cc-assign-tab${tab === key ? ' cc-assign-tab--active' : ''}`} onClick={() => setTab(key)}>
                  {icon}
                  <span className="cc-assign-tab__long">{label}</span>
                  <span className="cc-assign-tab__short" aria-hidden="true">{label.split(' ')[0]}</span>
                  <span className="cc-assign-tab__count">{count}</span>
                </button>
              </React.Fragment>))}
          </div>
        </div>
      </div>

      <div className="mc-cc-wrap">
        <div className="mc-cc-tabbed">
          {tab === 'calls' ? (<div className="cc-task-list comm-list comm-list--calls">
              <div className="cc-task-list__head">
                <span>Caller</span>
                <span>Direction</span>
                <span className="comm-col--opt">Duration</span>
                <span>Status</span>
                <span className="comm-col--opt">Summary</span>
                <span>When</span>
              </div>
              {visibleCalls.length === 0 ? (<div className="mc-cc-empty">{q ? 'No calls match your search' : 'No recent calls'}</div>) : visibleCalls.map((c, i) => {
                const name = [c.firstName, c.lastName].filter(Boolean).join(' ') || c.phoneNumber || 'Unknown';
                return (<div key={c.callSid || i} className="cc-task-row comm-row--static">
                    <span className="cc-task-row__title" title={c.phoneNumber || undefined}>{name}</span>
                    <span className="cc-task-row__meta">{c.direction || '—'}</span>
                    <span className="cc-task-row__meta comm-col--opt">{c.callDurationSecs ? `${c.callDurationSecs}s` : '—'}</span>
                    <span className="cc-task-row__meta">{c.callStatus || '—'}</span>
                    <span className="cc-task-row__meta comm-col--opt" title={c.transcriptSummary || undefined}>{c.transcriptSummary || '—'}</span>
                    <span className="cc-task-row__created">
                      <FiClock size={12}/>
                      <span className="cc-task-row__clip">{relTime(c.created)}</span>
                    </span>
                  </div>);
            })}
            </div>) : (<div className="cc-task-list comm-list" ref={listRef}>
              <div className="cc-task-list__head">
                <span>ID</span>
                <span>Channel</span>
                <span>Title</span>
                <span className="comm-col--opt">Insured</span>
                <span>Status</span>
                <span>Updated</span>
                <span className="comm-col--opt">Activity</span>
                <span />
              </div>
              {loading && visibleItems.length === 0 && <SkeletonRows count={6}/>}
              {!loading && loadError && visibleItems.length === 0 && <div className="mc-cc-empty">{loadError}</div>}
              {!loading && !loadError && visibleItems.length === 0 && !hasMore && (<div className="mc-cc-empty">
                  {search ? 'No threads match your search' : 'Nothing here yet — new email and voice activity will appear automatically.'}
                </div>)}
              {visibleItems.map(item => (<div key={item.taskId} className="cc-task-row" role="button" tabIndex={0} aria-label={`Open ${item.taskCode} ${item.title}`} onClick={() => openItem(item)} onKeyDown={e => {
                    if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) {
                        e.preventDefault();
                        openItem(item);
                    }
                }}>
                  <span className="cc-task-row__code">{item.taskCode}</span>
                  <span className={`cc-task-row__type comm-channel comm-channel--${item.source}`}>
                    {item.source === 'email' ? <FiMail size={11}/> : <FiPhoneCall size={11}/>}
                    <span className="cc-task-row__clip">{item.source === 'email' ? 'Email' : 'Voice'}</span>
                  </span>
                  <span className="cc-task-row__title" title={item.lastMessage?.message || item.description || item.title}>{item.title}</span>
                  <span className="cc-task-row__meta comm-col--opt" title={item.linkedInsuredName || undefined}>{item.linkedInsuredName || '—'}</span>
                  <span className="cc-task-row__status">
                    <span className="cc-task-row__status-dot" style={{ background: STATUS_COLOR[item.status] }}/>
                    <span className="cc-task-row__clip">{STATUS_LABEL[item.status]}</span>
                  </span>
                  <span className="cc-task-row__created">
                    <FiClock size={12}/>
                    <span className="cc-task-row__clip">{relTime(item.updatedAt || item.createdAt)}</span>
                  </span>
                  <div className="cc-task-row__stats comm-col--opt">
                    <span className="cc-task-row__stat" title="Responses">
                      <FiMessageSquare size={12}/>
                      {item.commentCount}
                    </span>
                  </div>
                  <div className="cc-task-row__actions">
                    <button className="mc-task-action-btn" onClick={e => {
                    e.stopPropagation();
                    window.open(`${window.location.origin}${taskHref(item)}`, '_blank', 'noopener,noreferrer');
                }} title="Open in new tab" aria-label="Open in new tab">
                      <FiExternalLink size={12}/>
                    </button>
                  </div>
                </div>))}
              <div ref={sentinelRef} style={{ height: 1 }}/>
              {(loadingMore || (!loading && hasMore && visibleItems.length < MIN_VISIBLE_ROWS)) && (<div className="mc-loading-more" role="status">
                  <FiRefreshCw size={12} className="mc-spin" aria-hidden="true"/>
                  Loading more…
                </div>)}
            </div>)}
        </div>
      </div>
    </div>);
};
export default CommunicationCenterPage;
